import TimelineObject from './TimelineObject';
import {Timeline} from "./Timeline";
import store, { StoreActions } from '../../state/store';
import ColorUtils from '../utilities/ColorUtils';
import { assert } from '../../classes/utilities/ErrorHandling';
import Engine from '../../classes/Engine';
import TimelineChannel, { TimelineChannelSaveData } from '../../classes/timeline/TimelineChannel';
import { TimelineTrackTypes } from '../../globals';
import { TimelineElementTypes } from '../../interfaces/Timeline';
import { getUuidv4 } from '../utilities/Misc';
import { timelineElementMap } from '.';

interface TimelineTrackDataObject {
	trackId: string;
	timelineId: string;
	name: string;
}

export interface TimelineTrackSaveObject {
	channels: TimelineChannelSaveData[];
	hidden: boolean;
	id: string,
	timelineId: string,
	name: string,
	type: TimelineTrackTypes,
	uiColor: string,
	objects: any[] // TODO this should be typed
	persistentSceneObjectId?: string;
	objectGroupGuid?: string;
}

const timelineTrackDataObject: TimelineTrackDataObject = {
	trackId: "",
	timelineId: "",
	name: "",
};

export default class TimelineTrack
{
	#id: string;
	#timeline: Timeline;
	objects: TimelineObject[];
	type: TimelineTrackTypes;
	uiInfo: {
		color: string;
		isSelected: boolean;
		isExpanded: boolean;
		isRenaming: boolean;
	};

	constructor(
		name: string,
		timeline: Timeline,
		type = TimelineTrackTypes.ObjectsTrack,
		createActionChannelObject = true
	)
	{
		this.#id = getUuidv4();
		this.#timeline = timeline;
		this.objects = [];
		this.type = type;

		const dataObject = {
			...timelineTrackDataObject,
			trackId: this.id,
			timelineId: this.timeline.id,
			name: name,
		};

		store.dispatch(
			StoreActions.timeline.setElementData({ dataId: this.id, data: dataObject })
		);

		this.uiInfo = {
			color: ColorUtils.getRandomColorRGB(),
			isSelected: false,
			isExpanded: true,
			isRenaming: false,
		};

		timelineElementMap.set(this.id, this);

		if (
			type === TimelineTrackTypes.ActionsTrack &&
			createActionChannelObject
		)
		{
			const sceneRoot = Engine.getInstance().getSelectionRoot(
				timeline.associatedSceneId
			);
			const timelineObject = new TimelineObject({
				track: this,
				sceneObjectReference: sceneRoot,
				skipChannelCreation: true,
			});
			timelineObject.channels.push(
				new TimelineChannel("Actions Channel", timelineObject)
			);
			this.objects.push(timelineObject);
		}
	}

	getDataObject()
	{
		return store.getState().timelineModule.elementData[this.id];
	}

	get id()
	{
		return this.#id;
	}

	get timeline()
	{
		return this.#timeline;
	}

	get name()
	{
		return this.getDataObject().name;
	}

	set name(name: string)
	{
		store.dispatch(
			StoreActions.timeline.setElementProperty({
				elementId: this.id,
				propertyName: "name",
				propertyValue: name,
			})
		);
	}

	clone(newTimeline: any, fullTimelineClone = false): TimelineTrack
	{
		const newTrack = new TimelineTrack(this.name, newTimeline, this.type, false);
		newTrack.uiInfo = Object.assign({}, this.uiInfo);

		for (const oldObject of this.objects)
		{
			newTrack.objects.push(oldObject.clone(newTrack, fullTimelineClone));
		}

		return newTrack;
	}

	save(shouldIncludeChildren: boolean): TimelineTrackSaveObject
	{
		const output: TimelineTrackSaveObject = {
			channels: [],
			hidden: false,
			id: this.id,
			timelineId: this.timeline.id,
			name: this.name,
			type: this.type,
			uiColor: this.uiInfo.color,
			objects: []
		};

		if (shouldIncludeChildren)
		{
			output.objects = [];
			this.objects.forEach((o) =>
				output.objects.push(o.save(shouldIncludeChildren))
			);
		}

		return output;
	}

	load(savedData : TimelineTrackSaveObject)
	{
		store.dispatch(StoreActions.timeline.reparentElementData({ dataId: this.id, newDataId: savedData.id }));
		store.dispatch(StoreActions.timeline.setElementProperty({ elementId: savedData.id, propertyName: "trackId", propertyValue: savedData.id }));

		this.#id = savedData.id;
		timelineElementMap.set(this.id, this);

		this.name = savedData.name;
		this.uiInfo.color = savedData.uiColor;
		if(savedData.type) this.type = savedData.type;

		if (savedData.objects)
		{
			for (const savedObjectData of savedData.objects)
			{
				if (savedObjectData.hidden && !savedObjectData.persistentSceneObjectId && !savedObjectData.objectGroupGuid) continue;

				if (savedObjectData.hidden)
				{
					const hiddenChannels : TimelineChannel[] = [];
					const visibileChannels : TimelineChannel[] = [];
					for (const channel of savedObjectData.channels)
					{
						if (channel.fragments)
						{
							if (channel.fragments[0].startTime === channel.fragments[0].endTime)
							{
								hiddenChannels.push(channel);
							}
							else
							{
								visibileChannels.push(channel);
							}
						}
					}

					//hidden timeline objects should not have any visibile channels. this only happens as a result of an old bug..
					//in this case, we split the object into 2 objects, an hidden one with the hidden channels and a visible one with the visible ones
					if (visibileChannels.length)
					{
						const params = { track: this };

						const hiddenObjectData = JSON.parse(JSON.stringify(savedObjectData));
						const visibleObjectData = JSON.parse(JSON.stringify(savedObjectData));

						hiddenObjectData.channels = hiddenChannels;
						visibleObjectData.channels = visibileChannels;
						visibleObjectData.id = getUuidv4();
						visibleObjectData.hidden = false;

						const hiddenObject = new TimelineObject(params);
						hiddenObject.load(hiddenObjectData);
						const visibleObject = new TimelineObject(params);
						visibleObject.load(visibleObjectData);

						this.objects.push(visibleObject);
						this.objects.push(hiddenObject);

						continue;
					}
				}

				if(this.type === TimelineTrackTypes.ActionsTrack && this.objects.length)
				{
					const sceneRootObject = this.objects[0];
					delete savedObjectData.persistentSceneObjectId;
					sceneRootObject.load(savedObjectData);
				}
				else
				{
					const params = { track: this };
					const loadedObject = new TimelineObject(params);
					loadedObject.load(savedObjectData);
					this.objects.push(loadedObject);
				}
			}
		}
	}

	export()
	{

	}

	getType()
	{
		return TimelineElementTypes.Track;
	}

	getParent()
	{
		return this.timeline;
	}

	getChildren()
	{
		return this.objects;
	}

	setChildren(children: TimelineObject[])
	{
		this.objects = children;
	}

	getName()
	{
		return this.name;
	}

	deleteSelf()
	{

		for (let i = this.objects.length - 1; i >= 0; i--)
		{
			this.objects[i].deleteSelf();
		}

		if(this.timeline)
		{
			switch(this.type)
			{
				case TimelineTrackTypes.ActionsTrack:
					this.timeline.actionsTrack = null;
					break;
				case TimelineTrackTypes.ObjectsTrack:
					this.timeline.objectsTrack = null;
					break;
				default:
					assert(false);
			}
		}

		timelineElementMap.delete(this.id);
	}

	onDelete()
	{
		this.getChildren().forEach((child) => child.onDelete());
	}

	onAdded()
	{
		this.getChildren().forEach((child) => child.onAdded());
	}
}
